import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, Save, Trash2, CheckCircle, Banknote, CreditCard, Scale, HelpCircle, AlertCircle, Calendar, MessageSquare, Users, Printer } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const Sales = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryDate = queryParams.get('date');

    const { stores, getStore, updateStore, user, getLocalISODate, currentDate, eventHistory, attendanceHistory } = useStore();
    const { showAlert } = useAlert();
    const store = getStore(id);
    const today = currentDate;
    const targetDate = queryDate || today;

    // 요일 계산 함수
    const getDayOfWeek = (dateStr) => {
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        const date = new Date(dateStr);
        return days[date.getDay()];
    };

    // 온도 가져오기 함수
    const fetchTemperature = async (dateStr) => {
        try {
            const isPast = new Date(dateStr) < new Date(new Date().toISOString().split('T')[0]);
            const url = isPast
                ? `https://archive-api.open-meteo.com/v1/archive?latitude=37.5665&longitude=126.9780&start_date=${dateStr}&end_date=${dateStr}&daily=temperature_2m_max&timezone=Asia%2FSeoul`
                : `https://api.open-meteo.com/v1/forecast?latitude=37.5665&longitude=126.9780&daily=temperature_2m_max&timezone=Asia%2FSeoul&start_date=${dateStr}&end_date=${dateStr}`;
            
            const response = await fetch(url);
            const data = await response.json();
            return data.daily?.temperature_2m_max?.[0] || null;
        } catch (error) {
            console.error('Temperature fetch failed:', error);
            return null;
        }
    };

    // 현재 시간 확인 (23:50 제한용)
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const isPastCutoff = currentTime.getHours() === 23 ? currentTime.getMinutes() >= 50 : currentTime.getHours() > 23;
    const isMyStore = user?.role === 'admin' || String(user?.storeId) === String(id);

    // 월간 마감 잠금 체크 (현재 월보다 이전이면 잠금) - 사용자 요청으로 비활성화
    // const todayObj = new Date(today);
    // const targetObj = new Date(targetDate);
    // const isMonthLocked = (targetObj.getFullYear() < todayObj.getFullYear()) ||
    //     (targetObj.getFullYear() === todayObj.getFullYear() && targetObj.getMonth() < todayObj.getMonth());
    const isMonthLocked = false;

    const canEdit = !isMonthLocked && ((user?.role === 'admin') || (targetDate === today && isMyStore && !isPastCutoff));
    const isReadOnlyWarning = !isMonthLocked && targetDate === today && isMyStore && isPastCutoff && user?.role !== 'admin';

    const [form, setForm] = useState({
        cash: '',
        card: '',
        variance: '',
        date: targetDate,
        notes: '',
        employeeCount: ''
    });

    const [receiptImage, setReceiptImage] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [temperatureCache, setTemperatureCache] = useState({}); // 온도 캐시

    useEffect(() => {
        const store = getStore(id);
        if (store) {
            // 타겟 날짜의 기록 찾기 (객체 형태인 salesHistory 우선 조회)
            const record = store.salesHistory?.[targetDate] || store.salesData?.find(s => s.date.startsWith(targetDate));

            if (record) {
                const newForm = {
                    cash: record.cash.toString(),
                    card: record.card.toString(),
                    variance: record.variance.toString(),
                    date: targetDate,
                    notes: record.notes || '',
                    employeeCount: record.employeeCount?.toString() || '',
                    eventName: record.eventName || '' // 행사명 추가
                };
                
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setForm(prev => {
                    if (JSON.stringify(prev) !== JSON.stringify(newForm)) return newForm;
                    return prev;
                });

                if (record.receiptImage) {
                    setReceiptImage(prev => {
                        if (prev !== record.receiptImage) return record.receiptImage;
                        return prev;
                    });
                }
            } else {
                // 데이터가 없으면 초기화 (새로 입력)
                const initialForm = { cash: '', card: '', variance: '', date: targetDate, notes: '', employeeCount: '' };
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setForm(prev => {
                    if (JSON.stringify(prev) !== JSON.stringify(initialForm)) return initialForm;
                    return prev;
                });
                
                setReceiptImage(prev => {
                    if (prev !== null) return null;
                    return prev;
                });
            }
        }
    }, [id, getStore, targetDate]);

    // 온도 캐시 로드 (관리자 테이블용)
    useEffect(() => {
        if (user?.role === 'admin' && store?.salesHistory) {
            const datesToFetch = Object.entries(store.salesHistory)
                .filter(([date, record]) => record.temperature === null || record.temperature === undefined)
                .map(([date]) => date);

            datesToFetch.forEach(async (date) => {
                if (!temperatureCache[date]) {
                    const temp = await fetchTemperature(date);
                    setTemperatureCache(prev => ({ ...prev, [date]: temp }));
                }
            });
        }
    }, [user?.role, store?.salesHistory]);

    // [v2.7] 상단 날짜(targetDate) 변경 시 하단 내역 필터(selectedMonth) 자동 동기화
    useEffect(() => {
        if (targetDate) {
            const yearMonth = targetDate.slice(0, 7); // "YYYY-MM"
            if (selectedMonth !== yearMonth) {
                setSelectedMonth(yearMonth);
            }
        }
    }, [targetDate]);

    // 수정 권한 체크 삭제 (상단으로 이동 및 로직 강화)


    const totalSales = (Number(form.cash) || 0) + (Number(form.card) || 0);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // 용량 최적화를 위해 이미지 압축 프로세스 진행
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    // 캔버스를 이용한 압축
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // 최대 너비 1000px 기준으로 비율 조정 (가독성 유지하며 용량 최적화)
                    const MAX_WIDTH = 1000;
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // JPEG 0.6 화질로 압축 (영수증 글자 식별 가능하면서 용량 대폭 감소)
                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
                    setReceiptImage(compressedBase64);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // [v2.3 강력 검증] 최우선 순위: 관리자 대리 입력 및 수정 시 비고 필수 체크
        const currentUserId = (user?.id || '').toLowerCase();
        const currentUserRole = (user?.role || '').toLowerCase();
        const isMasterOrAdmin = currentUserRole === 'admin' || currentUserId === 'master' || currentUserId.includes('admin');
        const existingRecord = store?.salesHistory?.[targetDate];
        const isEditing = !!existingRecord;

        if (isEditing || isMasterOrAdmin) {
            if (!form.notes || form.notes.trim() === '') {
                const alertMsg = isEditing 
                    ? '💡 보다 정확한 데이터 관리를 위해 하단 비고란에 구체적인 수정 사유를 입력해 주세요.\n(예: 오타 수정, 영수증 재확인 후 금액 변경 등)'
                    : '📢 관리자 대면/대리 입력 시에는 반드시 하단 비고란에 해당 사유를 입력해 주시기 바랍니다.\n(예: 매니저 요청 대리 입력, 누락 데이터 보충 등)';
                
                showAlert(alertMsg);
                return;
            }
            if (isEditing && form.notes.trim() === (existingRecord.notes || '').trim()) {
                showAlert('✍️ 이전 기록과 사유가 동일하여 저장이 차단됩니다. 변경된 부분에 대한 구체적인 사유를 추가로 작성해 주세요.\n(예: 사유 보충, 변경 사항 상세 기술 등)');
                return;
            }
        }

        if (!canEdit) {
            if (!isMyStore) {
                showAlert('본인 매장의 데이터만 입력할 수 있습니다.');
            } else {
                showAlert('과거 날짜의 매출은 관리자만 수정할 수 있습니다.');
            }
            return;
        }

        if (!receiptImage) {
            showAlert('1일 마감 영수증 사진을 반드시 첨부해야 합니다.');
            return;
        }

        if (!store) {
            showAlert('매장 정보를 찾을 수 없습니다.');
            return;
        }

        const todayStr = getLocalISODate();
        const temperature = await fetchTemperature(targetDate);

        // [v2.6] 이미지 서버 업로드 프로세스
        let finalImageUrl = receiptImage;
        if (receiptImage && receiptImage.startsWith('data:image')) {
            try {
                // StoreContext에서 API_BASE_URL을 가져올 수 없으므로 직접 구성하거나 전달받아야 함
                // 여기서는 StoreContext의 로직을 참고하여 구성
                const API_ROOT = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                    ? `http://${window.location.hostname}:3001/api`
                    : `${window.location.origin}/api`;

                const filename = `receipt_${id}_${targetDate}_${Date.now()}.jpg`;
                const uploadRes = await fetch(`${API_ROOT}/upload-image`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ image: receiptImage, filename })
                });

                if (uploadRes.ok) {
                    const uploadData = await uploadRes.json();
                    finalImageUrl = uploadData.url; // 서버 저장 경로 (예: /uploads/filename.jpg)
                } else {
                    throw new Error('Image upload failed');
                }
            } catch (err) {
                console.error('Image upload error:', err);
                showAlert('이미지 서버 업로드에 실패했습니다. 네트워크 상태를 확인해 주세요.');
                return;
            }
        }

        const newRecord = {
            date: new Date().toISOString(), // 정밀한 기록용 (ISO)
            localDate: todayStr, // 조회 및 필터링용 (현지 시간)
            cash: Number(form.cash),
            card: Number(form.card),
            total: totalSales,
            variance: Number(form.variance),
            notes: form.notes,
            employeeCount: Number(form.employeeCount) || 0,
            temperature: temperature, // 온도 추가
            receiptImage: finalImageUrl,
            isEdited: isEditing || existingRecord?.isEdited, // 이전의 수정 상태 유지 혹은 새로 수정됨 표시
            lastModified: isEditing ? new Date().toISOString() : null,
            modifiedBy: isEditing ? user?.role : null
        };

        let updatedSalesData = Array.isArray(store.salesData) ? [...store.salesData] : [];
        const todayIndex = updatedSalesData.findIndex(s => s && s.date && s.date.startsWith(todayStr));

        if (todayIndex >= 0) {
            updatedSalesData[todayIndex] = newRecord;
        } else {
            updatedSalesData.push(newRecord);
        }

        const dailyStatus = store.dailyStatus || {};
        const currentDayStatus = dailyStatus[todayStr] || {};
        const salesHistory = store.salesHistory || {};

        try {
            // 재고 데이터와 비교 검증
            const inventoryHistory = store.inventoryHistory || {};
            const todayInventory = inventoryHistory[targetDate];

            let inventoryStatus = currentDayStatus.inventory;

            if (todayInventory && newRecord.total > 0) {
                // 이전 기록 찾기
                const historyDates = Object.keys(inventoryHistory)
                    .filter(d => d < targetDate)
                    .sort((a, b) => b.localeCompare(a));
                const prevDate = historyDates[0];
                const prevInventory = prevDate ? inventoryHistory[prevDate] : null;

                if (prevInventory) {
                    const isTotalIdentical = JSON.stringify(todayInventory.stocks) === JSON.stringify(prevInventory.stocks);
                    if (isTotalIdentical) {
                        inventoryStatus = false; // 재고 변동이 없으면 '미완료'로 강제 변경
                        showAlert('⚠️ 경고: 매출이 발생했으나 입력된 재고가 이전 기록과 동일합니다.\n정확한 재고 파악을 위해 재고 상태를 [대기]로 변경합니다. 재고를 다시 확인해 주세요.');
                    }
                }
            }

            updateStore(id, {
                salesData: updatedSalesData,
                salesHistory: {
                    ...salesHistory,
                    [targetDate]: newRecord // targetDate 기준으로 정확히 저장
                },
                dailyStatus: {
                    ...dailyStatus,
                    [targetDate]: {
                        ...currentDayStatus,
                        sales: true,
                        inventory: inventoryStatus
                    }
                }
            });

            setIsSubmitted(true);

            // 성공 피드백을 위해 2초간 대기 후 폼 리셋 및 대시보드로 이동
            setTimeout(() => {
                setIsSubmitted(false);
                // 폼 초기화
                setForm({ cash: '', card: '', variance: '', date: todayStr, notes: '', employeeCount: '', eventName: '' });
                setReceiptImage(null);
                // 재고 현황으로 이동하여 다음 단계를 진행하게 함
                navigate(`/store/${id}/inventory`);
            }, 2000);
        } catch (error) {
            console.error('Save failed:', error);
            showAlert('저장 중 오류가 발생했습니다. 사진 용량이 너무 큰지 확인해 주세요.');
        }
    };

    const removeImage = () => {
        setReceiptImage(null);
    };

    const formattedDate = targetDate.replace(/-/g, '. ');

    return (
        <Layout>
            <div className="management-content">
                <div className="management-header-flex">
                    <h1>매출 관리 <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>v2.5</span> <span>- {store?.name || ''}</span></h1>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="mobile-store-name">{store?.name || ''}</div>
                        <div className="date-display-badge">
                            <Calendar size={18} />
                            <span>{formattedDate}</span>
                            {store?.salesHistory?.[targetDate]?.isEdited && (
                                <span style={{
                                    fontSize: '0.75rem',
                                    padding: '2px 8px',
                                    background: 'rgba(234, 179, 8, 0.25)',
                                    color: '#facc15',
                                    borderRadius: '6px',
                                    fontWeight: '800',
                                    marginLeft: '10px',
                                    border: '1px solid rgba(234, 179, 8, 0.3)'
                                }}>
                                    수정됨
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {isSubmitted && (
                    <div className="success-overlay">
                        <CheckCircle size={24} />
                        <span>데이터가 성공적으로 전송되었습니다!</span>
                    </div>
                )}

                {isMonthLocked && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(148, 163, 184, 0.15)', borderColor: '#94a3b8', color: '#94a3b8' }}>
                        <Save size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        해당 월의 데이터는 월간 마감이 완료되어 수정할 수 없는 보관 상태입니다.
                    </div>
                )}

                {/* 최근 3일 추이 요약 (모바일/PC 공통 3칸 레이아웃 강제) */}
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '20px',
                    width: '100%'
                }}>
                    {[1, 2, 3].map(daysAgo => {
                        // UTC 변환 오류 방지를 위해 현지 시간 기준으로 날짜 계산
                        const d = new Date(targetDate);
                        d.setDate(d.getDate() - daysAgo);
                        const dateStr = d.getFullYear() + '-' +
                            String(d.getMonth() + 1).padStart(2, '0') + '-' +
                            String(d.getDate()).padStart(2, '0');

                        const record = store?.salesHistory?.[dateStr];
                        return (
                            <div key={dateStr} className="glass-card" style={{
                                flex: 1,
                                padding: '10px 4px',
                                textAlign: 'center',
                                opacity: 0.8,
                                minWidth: 0, // Flex 자식 너축소 허용
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '2px' }}>
                                    {dateStr.slice(5).replace('-', '/')}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '700',
                                    color: record ? '#38bdf8' : '#64748b',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {record ? `₩${(record.total / 10000).toFixed(record.total >= 100000 ? 0 : 1)}만` : '데이터 없음'}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {isReadOnlyWarning && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(249, 115, 22, 0.15)', borderColor: '#f97316', color: '#f97316' }}>
                        <AlertCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        금일 보고는 23:50에 마감되었습니다. 이후 수정은 관리자에게 문의하세요.
                    </div>
                )}

                <div className="glass-card management-card">
                    <div className="card-header">
                        <h3 style={{ whiteSpace: 'nowrap' }}>{formattedDate} 매출 마감 입력</h3>
                        <div className="status-indicator-group responsive-grid" style={{
                            width: '100%',
                            marginTop: '8px'
                        }}>
                            <div className={`submit-badge ${store?.dailyStatus?.[targetDate]?.sales ? 'sales-done' : 'pending'}`} style={{ fontSize: '0.65rem', padding: '4px 2px', justifyContent: 'center', minWidth: 0, whiteSpace: 'nowrap' }}>
                                {store?.dailyStatus?.[targetDate]?.sales ? <><CheckCircle size={12} /> 매출</> : '매출 대기'}
                            </div>
                            <div className={`submit-badge ${store?.dailyStatus?.[targetDate]?.inventory ? 'inventory-done' : 'pending'}`} style={{ fontSize: '0.65rem', padding: '4px 2px', justifyContent: 'center', minWidth: 0, whiteSpace: 'nowrap' }}>
                                {store?.dailyStatus?.[targetDate]?.inventory ? <><CheckCircle size={12} /> 재고</> : '재고 대기'}
                            </div>
                            <div className={`submit-badge ${store?.dailyStatus?.[targetDate]?.facility ? 'inventory-done' : 'pending'}`} style={{ fontSize: '0.65rem', padding: '4px 2px', justifyContent: 'center', minWidth: 0, whiteSpace: 'nowrap', background: store?.dailyStatus?.[targetDate]?.facility ? 'rgba(168, 85, 247, 0.2)' : undefined, color: store?.dailyStatus?.[targetDate]?.facility ? '#a855f7' : undefined }}>
                                {store?.dailyStatus?.[targetDate]?.facility ? <><CheckCircle size={12} /> 시설</> : '시설 대기'}
                            </div>
                            <div className={`submit-badge ${store?.dailyStatus?.[targetDate]?.order ? 'sales-done' : 'pending'}`} style={{ fontSize: '0.65rem', padding: '4px 2px', justifyContent: 'center', minWidth: 0, whiteSpace: 'nowrap' }}>
                                {store?.dailyStatus?.[targetDate]?.order ? <><CheckCircle size={12} /> 발주</> : '발주 대기'}
                            </div>
                        </div>
                    </div>

                    <form className="simplified-sales-form" onSubmit={handleSubmit}>
                        <div className="sales-input-grid">
                            <div className="input-field">
                                <label>현금 매출 합계</label>
                                <div className="input-wrapper">
                                    <Banknote className="input-icon" size={20} />
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={form.cash}
                                        onChange={e => setForm({ ...form, cash: e.target.value })}
                                        required
                                        readOnly={!canEdit}
                                    />
                                </div>
                            </div>
                            <div className="input-field">
                                <label>카드 매출 합계</label>
                                <div className="input-wrapper">
                                    <CreditCard className="input-icon" size={20} />
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={form.card}
                                        onChange={e => setForm({ ...form, card: e.target.value })}
                                        required
                                        readOnly={!canEdit}
                                    />
                                </div>
                            </div>
                            <div className="input-field">
                                <label>과부족 (정산 차액)</label>
                                <div className="input-wrapper">
                                    <Scale className="input-icon" size={20} />
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={form.variance}
                                        onChange={e => setForm({ ...form, variance: e.target.value })}
                                        required
                                        readOnly={!canEdit}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="total-sales-highlight">
                            <span className="total-label">매출 합계(현금+카드)</span>
                            <span className="total-value">₩ {totalSales.toLocaleString()}</span>
                        </div>

                        <div className="notes-section">
                            <label><Users size={18} /> 금일 근무인원 현황</label>
                            <div className="input-wrapper" style={{ position: 'relative' }}>
                                <input
                                    type="number"
                                    placeholder="0"
                                    value={form.employeeCount}
                                    onChange={e => setForm({ ...form, employeeCount: e.target.value })}
                                    required
                                    readOnly={!canEdit}
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem' }}
                                />
                                <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>명</span>
                            </div>
                        </div>

                        <div className="notes-section">
                            <label>
                                <MessageSquare size={18} /> 
                                특이사항 {store?.salesHistory?.[targetDate] ? <span style={{ color: '#ef4444' }}>(수정 시 필수입력)</span> : <span style={{ color: '#64748b' }}>(선택사항)</span>}
                            </label>
                            <textarea
                                className="notes-textarea"
                                placeholder="매출과 관련된 특이사항이 있다면 입력해 주세요. (예: 단체 주문 건, 정산 오차 원인 등)"
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                                disabled={!canEdit}
                            />
                        </div>

                        <div className="receipt-upload-section" style={{ opacity: canEdit ? 1 : 0.7 }}>
                            <label className="upload-label">1일 마감 영수증 첨부</label>
                            {!receiptImage ? (
                                <div className={`upload-box ${!canEdit ? 'disabled' : ''}`} onClick={() => canEdit && document.getElementById('receipt-input').click()}>
                                    <Camera size={40} color="#38bdf8" />
                                    <p>영수증 사진을 찍거나 파일을 선택하세요</p>
                                    <span style={{ fontSize: '0.8rem', color: '#475569' }}>최대 10MB, JPG/PNG 가능</span>
                                    <input
                                        id="receipt-input"
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                        disabled={!canEdit}
                                    />
                                </div>
                            ) : (
                                <div className="receipt-preview">
                                    <img src={receiptImage} alt="Receipt Preview" />
                                    {canEdit && (
                                        <button type="button" className="remove-image-btn" onClick={removeImage}>
                                            <Trash2 size={16} /> 삭제하기
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {canEdit ? (
                            <>
                                <button type="submit" className="full-width-button">
                                    {store?.name || ''} 마감 제출하기
                                </button>
                                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#64748b', marginTop: '12px' }}>
                                    * 영수증 사진을 첨부해야 제출이 가능합니다.
                                </p>
                            </>
                        ) : (
                            <div className="admin-only-alert" style={{ textAlign: 'center', padding: '16px', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', color: '#ef4444', border: '1px solid #ef4444' }}>
                                <AlertCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                익일 이후 매출 수정은 관리자만 가능합니다.
                            </div>
                        )}
                    </form>
                </div>

                <div className="glass-card info-card" style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <HelpCircle size={18} color="#38bdf8" />
                        <h4 style={{ margin: 0 }}>도움말</h4>
                    </div>
                    <p>현장에서 정산 후 현금, 카드 매출을 입력하고 반드시 1일 마감 영수증 사진을 첨부하여 제출해 주세요. 제출된 정보는 해당 매장의 관리 데이터로 안전하게 저장됩니다.</p>
                </div>

                {/* Admin Exclusive: Detailed Sales History Table (PC View) */}
                {user?.role === 'admin' && (
                    <div className="admin-wide-report recent-activities desktop-only is-admin">
                        <div className="activities-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3>{store?.name || ''} 최근 매출 내역 (관리자 확인용)</h3>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    padding: '4px 12px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                    <Calendar size={16} color="#94a3b8" />
                                    {/* 연도 선택 드롭다운 */}
                                    <select
                                        value={selectedMonth.split('-')[0]}
                                        onChange={(e) => {
                                            const newYear = e.target.value;
                                            const currentMonth = selectedMonth.split('-')[1];
                                            setSelectedMonth(`${newYear}-${currentMonth}`);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#f8fafc',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            outline: 'none',
                                            cursor: 'pointer',
                                            padding: '4px'
                                        }}
                                    >
                                        {['2024', '2025', '2026'].map(y => <option key={y} value={y} style={{ background: '#1e293b' }}>{y}년</option>)}
                                    </select>
                                    
                                    {/* 월 선택 드롭다운 */}
                                    <select
                                        value={selectedMonth.split('-')[1]}
                                        onChange={(e) => {
                                            const newMonth = e.target.value;
                                            const currentYear = selectedMonth.split('-')[0];
                                            setSelectedMonth(`${currentYear}-${newMonth}`);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#f8fafc',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            outline: 'none',
                                            cursor: 'pointer',
                                            padding: '4px'
                                        }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                                            <option key={m} value={m} style={{ background: '#1e293b' }}>{Number(m)}월</option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    onClick={() => window.print()}
                                    className="icon-button"
                                    style={{
                                        padding: '8px 16px',
                                        background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                                        color: 'white',
                                        fontWeight: '700',
                                        gap: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: 'none',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <Printer size={16} /> 인쇄
                                </button>
                            </div>
                        </div>
                        <div className="activities-list is-admin">
                            <div className="activity-item header-item" style={{
                                gridTemplateColumns: '0.9fr 0.4fr 1.2fr 0.5fr 0.6fr 1fr 1fr 1fr 0.7fr 0.7fr 1fr 80px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                padding: '12px 20px',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <div>날짜</div>
                                <div style={{ textAlign: 'center' }}>요일</div>
                                <div>행사명</div>
                                <div style={{ textAlign: 'center' }}>온도</div>
                                <div style={{ textAlign: 'center' }}>입장객</div>
                                <div style={{ textAlign: 'right' }}>현금</div>
                                <div style={{ textAlign: 'right' }}>카드</div>
                                <div style={{ textAlign: 'right' }}>합계</div>
                                <div style={{ textAlign: 'center' }}>과부족</div>
                                <div style={{ textAlign: 'center' }}>증감(%)</div>
                                <div>특이사항</div>
                                <div style={{ textAlign: 'center' }}>영수증</div>
                            </div>
                            {store?.salesHistory ? (
                                (() => {
                                    // 날짜 정규화 헬퍼 (YYYY-MM-DD 보장)
                                    const normalizeDate = (dStr) => {
                                        if (!dStr) return '';
                                        const dt = dStr.split('T')[0];
                                        const [y, m, d] = dt.split('-');
                                        if (!y || !m || !d) return dt;
                                        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                                    };
                                    // 1. 전체 히스토리를 오름차순(과거->최신)으로 정렬하여 준비
                                    const allHistoryEntries = Object.entries(store.salesHistory)
                                        .sort((a, b) => normalizeDate(a[0]).localeCompare(normalizeDate(b[0])));

                                    // 2. 전체 히스토리에 대해 증감율(growth) 및 신규 여부 계산
                                    const currentYear = selectedMonth.split('-')[0];
                                    const currentMonthInt = parseInt(selectedMonth.split('-')[1]);
                                    
                                    // 해당 매장의 올해 이전 달들에 매출이 있었는지 확인
                                    const hasSalesInPreviousMonths = allHistoryEntries.some(([date, record]) => {
                                        const d = normalizeDate(date);
                                        if (!d.startsWith(currentYear)) return false;
                                        const mInt = parseInt(d.split('-')[1]);
                                        return mInt < currentMonthInt && (Number(record.total) || 0) > 0;
                                    });

                                    let yearFirstSalesFound = hasSalesInPreviousMonths;

                                    const allWithGrowth = allHistoryEntries.map((entry, index) => {
                                        const [date, record] = entry;
                                        let growth = null;
                                        let isNew = false;

                                        // 해당 연도(currentYear)의 첫 매출인지 확인
                                        const total = Number(record.total) || 0;
                                        if (date.startsWith(currentYear) && total > 0 && !yearFirstSalesFound) {
                                            isNew = true;
                                            yearFirstSalesFound = true;
                                        }

                                        if (index > 0) {
                                            const prevRecord = allHistoryEntries[index - 1][1];
                                            growth = ((record.total - prevRecord.total) / (prevRecord.total || 1)) * 100;
                                        }

                                        // 신규인 경우 증감율 0 처리 (Analytics와 통일)
                                        if (isNew) growth = 0;

                                        return { date, record, growth, isNew };
                                    });

                                    // 3. 계산된 결과에서 현재 선택된 월(selectedMonth)에 해당하는 데이터만 필터링
                                    const filteredList = allWithGrowth
                                        .filter(item => normalizeDate(item.date).startsWith(selectedMonth))
                                        .reverse(); // 화면 표시를 위해 최신순(내림차순)으로 정렬

                                    if (filteredList.length === 0) {
                                        return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>해당 월의 매출 내역이 없습니다.</div>;
                                    }

                                    return filteredList.map(({ date, record, growth, isNew }) => (
                                        <div key={date} className="activity-item row-item" style={{
                                            gridTemplateColumns: '0.9fr 0.4fr 1.2fr 0.5fr 0.6fr 1fr 1fr 1fr 0.7fr 0.7fr 1fr 80px',
                                            padding: '16px 20px',
                                            alignItems: 'center',
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                        }}>
                                            <div style={{ fontWeight: '600', color: date === targetDate ? '#38bdf8' : '#f8fafc', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {date}
                                                {record.isEdited && (
                                                    <span style={{ 
                                                        fontSize: '0.65rem', 
                                                        padding: '2px 4px', 
                                                        background: 'rgba(234, 179, 8, 0.2)', 
                                                        color: '#facc15', 
                                                        borderRadius: '4px',
                                                        fontWeight: '700',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        수정됨
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>{getDayOfWeek(date)}</div>
                                            <div style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>{eventHistory[date] || '-'}</div>
                                            <div style={{ textAlign: 'center', color: '#fbbf24', fontSize: '0.85rem' }}>
                                                {(() => {
                                                    const temp = record.temperature ?? temperatureCache[date];
                                                    return temp !== null && temp !== undefined ? `${Math.round(temp)}°` : '-';
                                                })()}
                                            </div>
                                            <div style={{ textAlign: 'center', color: '#94a3b8' }}>{(attendanceHistory[date] || 0).toLocaleString()}명</div>
                                            <div style={{ textAlign: 'right', color: '#cbd5e1' }}>₩{record.cash.toLocaleString()}</div>
                                            <div style={{ textAlign: 'right', color: '#cbd5e1' }}>₩{record.card.toLocaleString()}</div>
                                            <div style={{ textAlign: 'right', fontWeight: '700', color: '#38bdf8' }}>₩{record.total.toLocaleString()}</div>
                                            <div style={{ textAlign: 'center', color: record.variance < 0 ? '#f87171' : (record.variance > 0 ? '#4ade80' : '#94a3b8') }}>
                                                {record.variance > 0 ? '+' : ''}{record.variance.toLocaleString()}
                                            </div>
                                            <div style={{ 
                                                textAlign: 'center', 
                                                fontWeight: '600',
                                                fontSize: '0.85rem',
                                                color: (isNew || (growth !== null && record.total > 0 && growth > 0)) ? '#22c55e' : (growth !== null && record.total > 0 && growth < 0 ? '#ef4444' : '#64748b')
                                            }}>
                                                {isNew ? '신규' : (
                                                    (growth !== null && record.total > 0) ? (
                                                        <>
                                                            {growth > 0 ? '▲' : (growth < 0 ? '▼' : '')} 
                                                            {Math.abs(growth) > 999 ? '999%+' : `${Math.abs(growth).toFixed(1)}%`}
                                                         </>
                                                    ) : '-'
                                                )}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.notes}>
                                                {record.notes || '-'}
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                {record.receiptImage && (
                                                    <button
                                                        onClick={() => {
                                                            const newWindow = window.open('', '_blank');
                                                            if (newWindow) {
                                                                newWindow.document.write(`
                                                                    <!DOCTYPE html>
                                                                    <html>
                                                                    <head>
                                                                        <title>영수증</title>
                                                                        <style>
                                                                            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #0f172a; }
                                                                            img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                                                        </style>
                                                                    </head>
                                                                    <body>
                                                                        <img src="${record.receiptImage}" alt="영수증" />
                                                                    </body>
                                                                    </html>
                                                                `);
                                                                newWindow.document.close();
                                                            }
                                                        }}
                                                        className="icon-button"
                                                        title="영수증 원본 보기"
                                                        style={{ padding: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#38bdf8' }}
                                                    >
                                                        <ImageIcon size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ));
                                })()
                            ) : (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>이전 매출 내역이 없습니다.</div>
                            )}

                            {/* 합계 행 추가 */}
                            {store?.salesHistory && Object.entries(store.salesHistory).filter(([date]) => date.startsWith(selectedMonth)).length > 0 && (
                                <div className="activity-item total-row" style={{
                                    gridTemplateColumns: '0.9fr 0.4fr 1.2fr 0.5fr 0.6fr 1fr 1fr 1fr 0.7fr 0.7fr 1fr 80px',
                                    padding: '16px 20px',
                                    alignItems: 'center',
                                    borderTop: '3px solid rgba(56, 189, 248, 0.3)',
                                    background: 'rgba(56, 189, 248, 0.1)',
                                    fontWeight: '700'
                                }}>
                                    <div style={{ color: '#38bdf8', fontSize: '0.95rem' }}>총 합계</div>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                    <div style={{ textAlign: 'center', color: '#38bdf8' }}>
                                        {Object.entries(store.salesHistory)
                                            .filter(([date]) => date.startsWith(selectedMonth))
                                            .reduce((sum, [date]) => sum + (Number(attendanceHistory[date]) || 0), 0).toLocaleString()}명
                                    </div>
                                    <div style={{ textAlign: 'right', color: '#f8fafc', fontSize: '0.95rem' }}>
                                        ₩{Object.entries(store.salesHistory)
                                            .filter(([date]) => date.startsWith(selectedMonth))
                                            .reduce((sum, [, record]) => sum + record.cash, 0).toLocaleString()}
                                    </div>
                                    <div style={{ textAlign: 'right', color: '#f8fafc', fontSize: '0.95rem' }}>
                                        ₩{Object.entries(store.salesHistory)
                                            .filter(([date]) => date.startsWith(selectedMonth))
                                            .reduce((sum, [, record]) => sum + record.card, 0).toLocaleString()}
                                    </div>
                                    <div style={{ textAlign: 'right', color: '#38bdf8', fontSize: '1.05rem' }}>
                                        ₩{Object.entries(store.salesHistory)
                                            .filter(([date]) => date.startsWith(selectedMonth))
                                            .reduce((sum, [, record]) => sum + record.total, 0).toLocaleString()}
                                    </div>
                                    <div style={{ 
                                        textAlign: 'center', 
                                        fontSize: '0.95rem',
                                        color: (() => {
                                            const totalVariance = Object.entries(store.salesHistory)
                                                .filter(([date]) => date.startsWith(selectedMonth))
                                                .reduce((sum, [, record]) => sum + record.variance, 0);
                                            return totalVariance < 0 ? '#f87171' : (totalVariance > 0 ? '#4ade80' : '#94a3b8');
                                        })()
                                    }}>
                                        {(() => {
                                            const totalVariance = Object.entries(store.salesHistory)
                                                .filter(([date]) => date.startsWith(selectedMonth))
                                                .reduce((sum, [, record]) => sum + record.variance, 0);
                                            return (totalVariance > 0 ? '+' : '') + totalVariance.toLocaleString();
                                        })()}
                                    </div>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Sales;


